import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import AdvancedSurveysModule from "../TrialAccordions/AdvancedSurveysModule";

export const DEFAULT_EDIARY_CONFIG = {
  name: "eDiary",
  name_de: "",
  name_es: "",
  interval_hours: 24,
  occurrence_count: 7,
  window_hours: 12,
  allow_investigator_start_date_edit: false,
};

export const validateEdiaryConfig = (config) => {
  if (!config) return "Enter the eDiary settings.";
  if (!config.name?.trim()) return "The eDiary needs a name.";

  const interval = Number(config.interval_hours);
  const count = Number(config.occurrence_count);
  const window = Number(config.window_hours);

  if (!Number.isInteger(interval) || interval < 1) {
    return "The eDiary interval must be at least 1 hour.";
  }
  if (!Number.isInteger(count) || count < 1) {
    return "The eDiary must have at least 1 occurrence.";
  }
  if (!Number.isInteger(window) || window < 1) {
    return "The eDiary window must be at least 1 hour.";
  }
  if (window > interval) {
    return "The eDiary window cannot be longer than the interval.";
  }
  return "";
};

const EDiaryConfigSection = ({ config, onChange, role, isLocked }) => {
  if (!config) return null;

  const handleField = (event) => {
    const { name, value } = event.target;
    onChange({ ...config, [name]: value });
  };

  const error = validateEdiaryConfig(config);

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="ediary-config-section">
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        eDiary
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 2 }}>
        A recurring survey diary scheduled automatically for every subject, starting on the day the
        subject is added. Investigators cannot change individual dates.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
        <TextField
          size="small"
          label="Name (English)"
          name="name"
          value={config.name ?? ""}
          onChange={handleField}
          disabled={isLocked}
          required
        />
        <TextField
          size="small"
          label="Name (German)"
          name="name_de"
          value={config.name_de ?? ""}
          onChange={handleField}
          disabled={isLocked}
        />
        <TextField
          size="small"
          label="Name (Spanish)"
          name="name_es"
          value={config.name_es ?? ""}
          onChange={handleField}
          disabled={isLocked}
        />
        <TextField
          size="small"
          type="number"
          label="Repeat every (hours)"
          name="interval_hours"
          value={config.interval_hours ?? ""}
          onChange={handleField}
          disabled={isLocked}
          helperText="24 = daily, 48 = every 2 days, 168 = weekly"
          inputProps={{ min: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label="Number of eDiaries"
          name="occurrence_count"
          value={config.occurrence_count ?? ""}
          onChange={handleField}
          disabled={isLocked}
          inputProps={{ min: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label="Open for (hours)"
          name="window_hours"
          value={config.window_hours ?? ""}
          onChange={handleField}
          disabled={isLocked}
          helperText="How long each eDiary can be filled in after it opens"
          inputProps={{ min: 1 }}
        />
      </Box>

      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Checkbox
            checked={Boolean(config.allow_investigator_start_date_edit)}
            inputProps={{ "data-testid": "ediary-allow-start-date-checkbox-input" }}
            onChange={(event) =>
              onChange({ ...config, allow_investigator_start_date_edit: event.target.checked })
            }
            disabled={isLocked}
          />
        }
        label="Allow the investigator to change the eDiary start date"
      />

      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {config.visit_id ? (
          <AdvancedSurveysModule
            role={role}
            visitId={config.visit_id}
            selectedVisit={{ visit_number: -6 }}
            isTrialLocked={isLocked}
          />
        ) : (
          <Alert severity="info">
            Save the trial to choose the surveys that make up the eDiary.
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default EDiaryConfigSection;
